import React, { useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './SignupPage.css';

const defaultPlans = [
    {
        code: 'one_off_single_regatta',
        title: 'One-Off Regatta',
        amount: 49.95,
        meetingsIncluded: 1,
        recurring: false
    },
    {
        code: 'annual_unlimited',
        title: 'Annual Unlimited',
        amount: 79.95,
        meetingsIncluded: null,
        recurring: true
    }
];

function slugifyTenantKey(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
}

export function MultiStepSignupPage() {
    const { signUp, signInWithGoogle, signInWithAzure, signInWithFacebook, user } = useAuth();
    const history = useHistory();

    // Step management
    const [currentStep, setCurrentStep] = useState(1);

    // Step 1: Authentication
    const [authMethod, setAuthMethod] = useState(null); // 'social' | 'email'
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [googleLoading, setGoogleLoading] = useState(false);
    const [azureLoading, setAzureLoading] = useState(false);
    const [facebookLoading, setFacebookLoading] = useState(false);

    // Step 2: Account & Tenant
    const [adminName, setAdminName] = useState('');
    const [tenantName, setTenantName] = useState('');
    const [tenantKey, setTenantKey] = useState('');

    // Step 3: Regatta
    const [regattaName, setRegattaName] = useState('');
    const [regattaYear, setRegattaYear] = useState(new Date().getFullYear());
    const [meetingDate, setMeetingDate] = useState('');
    const [broeMeetingIdentifier, setBroeMeetingIdentifier] = useState('');
    const [broeAuthorizationKey, setBroeAuthorizationKey] = useState('');
    const [broeApiKey, setBroeApiKey] = useState('');
    const [skipRegatta, setSkipRegatta] = useState(false);

    // Step 4: Subscription
    const [plans, setPlans] = useState(defaultPlans);
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [planCode, setPlanCode] = useState('one_off_single_regatta');
    const [privacyAccepted, setPrivacyAccepted] = useState(false);

    // Global UI
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        document.title = 'Create Tenant | RegattaManager';
        ReactGA.event({ category: 'Signup', action: 'Signup Started' });
    }, []);

    useEffect(() => {
        if (!user) return;
        history.replace('/select-meeting');
    }, [user, history]);

    // Auto-sync tenant key from name
    useEffect(() => {
        if (!tenantKey.trim()) {
            setTenantKey(slugifyTenantKey(tenantName));
        }
    }, [tenantName, tenantKey]);

    // Load subscription plans
    useEffect(() => {
        const run = async () => {
            try {
                setLoadingPlans(true);
                const res = await fetch('/api/public/plans');
                if (!res.ok) return;
                const data = await res.json();
                if (Array.isArray(data?.plans) && data.plans.length > 0) {
                    setPlans(data.plans);
                    if (!data.plans.find(p => p.code === planCode)) {
                        setPlanCode(data.plans[0].code);
                    }
                }
            } catch {
                // Fallback to default plans
            } finally {
                setLoadingPlans(false);
            }
        };
        run();
    }, [planCode]);

    const selectedPlan = useMemo(() => plans.find(p => p.code === planCode) || plans[0], [plans, planCode]);

    // Social sign-up handlers
    const handleSocialSignup = async (provider, handler, setLoading) => {
        setLoading(true);
        setError('');
        ReactGA.event({ category: 'Signup', action: 'Social Auth Selected', label: provider });
        const { error } = await handler();
        if (error) {
            setError(error.message);
            setLoading(false);
        }
        // On success Supabase redirects — browser navigates away
    };

    const handleGoogleSignup = () => handleSocialSignup('Google', signInWithGoogle, setGoogleLoading);
    const handleAzureSignup = () => handleSocialSignup('Azure', signInWithAzure, setAzureLoading);
    const handleFacebookSignup = () => handleSocialSignup('Facebook', signInWithFacebook, setFacebookLoading);
    const providersLoading = googleLoading || azureLoading || facebookLoading || submitting;

    // Step 1 validation and next
    const validateStep1 = () => {
        if (authMethod === 'email') {
            if (!email.trim()) {
                setError('Email is required.');
                return false;
            }
            if (password.length < 6) {
                setError('Password must be at least 6 characters.');
                return false;
            }
            if (password !== confirmPassword) {
                setError('Passwords do not match.');
                return false;
            }
        }
        setError('');
        return true;
    };

    const handleStep1Next = async () => {
        if (!validateStep1()) return;
        if (authMethod === 'email') {
            setSubmitting(true);
            const signUpError = await signUp(email.trim(), password);
            setSubmitting(false);
            if (signUpError) {
                setError(signUpError);
                return;
            }

            ReactGA.event({ category: 'Signup', action: 'Step Completed', label: 'Email Signup Complete' });
            setNotice('Account created. Check your email to verify your account, then log in.');
            return;
        }

        ReactGA.event({ category: 'Signup', action: 'Step Completed', label: 'Step 1: Account' });
    };

    // Step 2 validation and next
    const validateStep2 = () => {
        if (!adminName.trim()) {
            setError('Admin name is required.');
            return false;
        }
        if (!tenantName.trim()) {
            setError('Tenant name is required.');
            return false;
        }
        const normalizedKey = slugifyTenantKey(tenantKey || tenantName);
        if (!normalizedKey) {
            setError('Tenant key cannot be empty.');
            return false;
        }
        setError('');
        return true;
    };

    const handleStep2Next = () => {
        if (!validateStep2()) return;
        ReactGA.event({ category: 'Signup', action: 'Step Completed', label: 'Step 2: Tenant Setup' });
        setCurrentStep(3);
    };

    // Step 3 next - can skip regatta
    const handleStep3Next = () => {
        if (skipRegatta) {
            ReactGA.event({ category: 'Signup', action: 'Step Completed', label: 'Step 3: Regatta Skipped' });
            setCurrentStep(4);
            return;
        }
        if (!regattaName.trim()) {
            setError('Regatta name is required.');
            return;
        }
        ReactGA.event({ category: 'Signup', action: 'Step Completed', label: 'Step 3: Regatta Added' });
        setError('');
        setCurrentStep(4);
    };

    // Step 4 - Final submission
    const handleFinalSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setNotice('');

        if (!selectedPlan) {
            setError('Please select a subscription plan.');
            return;
        }

        if (!privacyAccepted) {
            setError('Please confirm that you have read the Privacy Policy before continuing.');
            return;
        }

        const normalizedTenantKey = slugifyTenantKey(tenantKey || tenantName);
        if (!normalizedTenantKey) {
            setError('Tenant key is required.');
            return;
        }

        setSubmitting(true);
        try {
            // If using social auth, email won't be set yet - need to collect it at step 2
            // For this flow, we'll require email/password or social will have already set the account
            const setupEmail = authMethod === 'email' ? email.trim() : adminName;

            const response = await fetch('/api/public/stripe/checkout-session', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    adminName: adminName.trim(),
                    adminEmail: authMethod === 'email' ? email.trim().toLowerCase() : setupEmail,
                    tenantName: tenantName.trim(),
                    tenantKey: normalizedTenantKey,
                    regattaName: skipRegatta ? `${tenantName} Regatta` : regattaName.trim(),
                    regattaYear,
                    meetingDate,
                    broeMeetingIdentifier: broeMeetingIdentifier.trim(),
                    broeAuthorizationKey: broeAuthorizationKey.trim(),
                    broeApiKey: broeApiKey.trim(),
                    planCode: selectedPlan.code
                })
            });

            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                setError(payload?.message || 'Unable to create tenant right now.');
                return;
            }

            if (payload?.checkoutUrl) {
                ReactGA.event({ category: 'Signup', action: 'Proceeded to Payment', label: selectedPlan.code });
                window.location.assign(payload.checkoutUrl);
                return;
            }

            setNotice('Checkout session created. Please continue from the browser.');
        } catch (submitError) {
            setError(submitError?.message || 'Unexpected signup failure.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBack = () => {
        setError('');
        setNotice('');
        setCurrentStep(currentStep - 1);
    };

    const progressPercent = Math.max(25, Math.min(100, currentStep * 25));

    // Render steps
    const renderStep1 = () => (
        <div>
            {error && <div className="alert alert-danger">{error}</div>}
            {notice && <div className="alert alert-success">{notice}</div>}

            {authMethod === null ? (
                <>
                    <button
                        type="button"
                        className="btn btn-outline-dark w-100 mb-3 d-flex align-items-center justify-content-center gap-2 signup-provider-btn"
                        onClick={handleGoogleSignup}
                        disabled={providersLoading}
                    >
                        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                            <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.2l6.7-6.7C35.8 2.5 30.2 0 24 0 14.6 0 6.6 5.4 2.7 13.3l7.8 6C12.4 13 17.8 9.5 24 9.5z"/>
                            <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v8.5h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.5-4.1 7.1-10.2 7.1-17.1z"/>
                            <path fill="#FBBC05" d="M10.5 28.7A14.6 14.6 0 0 1 9.5 24c0-1.6.3-3.2.8-4.7l-7.8-6A24 24 0 0 0 0 24c0 3.8.9 7.4 2.5 10.6l8-5.9z"/>
                            <path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.4l-7.6-5.9c-2 1.4-4.6 2.2-7.4 2.2-6.2 0-11.5-4.2-13.5-9.9l-8 5.9C6.5 42.6 14.6 48 24 48z"/>
                        </svg>
                        {googleLoading ? 'Redirecting...' : 'Sign up with Google'}
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-secondary w-100 mb-3 d-flex align-items-center justify-content-center gap-2 signup-provider-btn"
                        onClick={handleAzureSignup}
                        disabled={providersLoading}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="currentColor" d="M11.4 0H0v11.4h11.4V0zM24 0H12.6v11.4H24V0zM11.4 12.6H0V24h11.4V12.6zM24 12.6H12.6V24H24V12.6z"/>
                        </svg>
                        {azureLoading ? 'Redirecting...' : 'Sign up with your Microsoft account'}
                    </button>

                    <button
                        type="button"
                        className="btn btn-outline-primary w-100 mb-3 d-flex align-items-center justify-content-center gap-2 signup-provider-btn"
                        onClick={handleFacebookSignup}
                        disabled={providersLoading}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                            <path fill="currentColor" d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.099 4.388 23.094 10.125 24v-8.438H7.078v-3.489h3.047V9.413c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.237 2.686.237v2.953h-1.514c-1.491 0-1.956.93-1.956 1.885v2.254h3.328l-.532 3.489h-2.796V24C19.612 23.094 24 18.099 24 12.073z"/>
                        </svg>
                        {facebookLoading ? 'Redirecting...' : 'Sign up with your Facebook account'}
                    </button>

                    <div className="signup-divider">
                        <hr className="flex-grow-1" />
                        <span className="px-2 text-muted" style={{ fontSize: '0.85em' }}>or</span>
                        <hr className="flex-grow-1" />
                    </div>

                    <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={() => {
                            ReactGA.event({ category: 'Signup', action: 'Social Auth Selected', label: 'email' });
                            setAuthMethod('email');
                            setError('');
                            setNotice('');
                        }}
                    >
                        Sign up with email
                    </button>
                </>
            ) : (
                <>
                    <form onSubmit={(e) => { e.preventDefault(); handleStep1Next(); }}>
                        <div className="mb-3">
                            <label className="form-label">Email</label>
                            <input
                                type="email"
                                className="form-control"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                autoComplete="email"
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Password</label>
                            <input
                                type="password"
                                className="form-control"
                                value={password}
                                onChange={e => setPassword(e.target.value)}
                                autoComplete="new-password"
                                required
                            />
                            <small className="text-muted">At least 6 characters</small>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Confirm Password</label>
                            <input
                                type="password"
                                className="form-control"
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                                required
                            />
                        </div>
                        <button type="submit" className="btn btn-primary w-100" disabled={submitting}>
                            {submitting ? 'Creating account...' : 'Create account'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-link w-100 mt-2"
                            onClick={() => {
                                setAuthMethod(null);
                                setEmail('');
                                setPassword('');
                                setConfirmPassword('');
                                setError('');
                            }}
                        >
                            Back to sign-up methods
                        </button>
                    </form>
                </>
            )}
        </div>
    );

    const renderStep2 = () => (
        <div>
            <h3 className="signup-step-title">Set up your account and club</h3>
            <p className="signup-step-copy">Add your details and choose your club subdomain.</p>

            {error && <div className="alert alert-danger">{error}</div>}
            {notice && <div className="alert alert-success">{notice}</div>}

            <form onSubmit={(e) => { e.preventDefault(); handleStep2Next(); }}>
                <div className="mb-3">
                    <label className="form-label">Your Name</label>
                    <input
                        type="text"
                        className="form-control"
                        value={adminName}
                        onChange={e => setAdminName(e.target.value)}
                        required
                    />
                </div>

                <div className="mb-3">
                    <label className="form-label">Club Name</label>
                    <input
                        type="text"
                        className="form-control"
                        value={tenantName}
                        onChange={e => setTenantName(e.target.value)}
                        required
                    />
                </div>

                <div className="mb-3">
                    <label className="form-label">Club Subdomain</label>
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            value={tenantKey}
                            onChange={e => setTenantKey(slugifyTenantKey(e.target.value))}
                            required
                        />
                        <span className="input-group-text">.regattamanager.co.uk</span>
                    </div>
                    <small className="text-muted">
                        Your URL: <strong>https://{tenantKey || 'your-club'}.regattamanager.co.uk/{regattaYear}</strong>
                    </small>
                </div>

                <div className="signup-actions-row mt-4 d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
                        Back
                    </button>
                    <button type="submit" className="btn btn-primary flex-grow-1">
                        Next
                    </button>
                </div>
            </form>
        </div>
    );

    const renderStep3 = () => (
        <div>
            <h3 className="signup-step-title">Create your first regatta</h3>
            <p className="signup-step-copy">Add your first regatta now, or skip and do it later.</p>

            {error && <div className="alert alert-danger">{error}</div>}
            {notice && <div className="alert alert-success">{notice}</div>}

            <form onSubmit={(e) => { e.preventDefault(); handleStep3Next(); }}>
                <div className="mb-3">
                    <label className="form-label">Regatta Name</label>
                    <input
                        type="text"
                        className="form-control"
                        value={regattaName}
                        onChange={e => setRegattaName(e.target.value)}
                        disabled={skipRegatta}
                        required={!skipRegatta}
                    />
                </div>

                <div className="row g-2 mb-3">
                    <div className="col-md-6">
                        <label className="form-label">Year</label>
                        <input
                            type="number"
                            min="2000"
                            max="2100"
                            className="form-control"
                            value={regattaYear}
                            onChange={e => setRegattaYear(Number(e.target.value || new Date().getFullYear()))}
                            disabled={skipRegatta}
                            required={!skipRegatta}
                        />
                    </div>
                    <div className="col-md-6">
                        <label className="form-label">Date <span className="text-muted">(optional)</span></label>
                        <input
                            type="date"
                            className="form-control"
                            value={meetingDate}
                            onChange={e => setMeetingDate(e.target.value)}
                            disabled={skipRegatta}
                        />
                    </div>
                </div>

                <details className="mb-3">
                    <summary className="text-muted" style={{ cursor: 'pointer' }}>BROE Integration (optional)</summary>
                    <div className="mt-3">
                        <div className="mb-2">
                            <label className="form-label">Meeting Identifier</label>
                            <input
                                type="text"
                                className="form-control"
                                value={broeMeetingIdentifier}
                                onChange={e => setBroeMeetingIdentifier(e.target.value)}
                                disabled={skipRegatta}
                            />
                        </div>
                        <div className="mb-2">
                            <label className="form-label">Authorization Key</label>
                            <input
                                type="text"
                                className="form-control"
                                value={broeAuthorizationKey}
                                onChange={e => setBroeAuthorizationKey(e.target.value)}
                                disabled={skipRegatta}
                            />
                        </div>
                        <div className="mb-2">
                            <label className="form-label">API Key</label>
                            <input
                                type="text"
                                className="form-control"
                                value={broeApiKey}
                                onChange={e => setBroeApiKey(e.target.value)}
                                disabled={skipRegatta}
                            />
                        </div>
                    </div>
                </details>

                <div className="form-check mb-3">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id="skipRegatta"
                        checked={skipRegatta}
                        onChange={e => {
                            setSkipRegatta(e.target.checked);
                            setError('');
                        }}
                    />
                    <label className="form-check-label" htmlFor="skipRegatta">
                        Skip for now, create regatta later
                    </label>
                </div>

                <div className="signup-actions-row mt-4 d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
                        Back
                    </button>
                    <button type="submit" className="btn btn-primary flex-grow-1">
                        Next
                    </button>
                </div>
            </form>
        </div>
    );

    const renderStep4 = () => (
        <div>
            <h3 className="signup-step-title">Choose your subscription</h3>
            <p className="signup-step-copy">Select a plan and continue to checkout.</p>

            {error && <div className="alert alert-danger">{error}</div>}
            {notice && <div className="alert alert-success">{notice}</div>}

            <form onSubmit={handleFinalSubmit}>
                <div className="mb-4">
                    {loadingPlans && <p className="text-muted">Loading plans...</p>}
                    <div className="d-flex flex-column gap-2">
                        {plans.map(plan => (
                            <label
                                key={plan.code}
                                className={`signup-plan-card border rounded p-3 d-flex justify-content-between align-items-start${planCode === plan.code ? ' is-selected' : ''}`}
                            >
                                <div>
                                    <input
                                        type="radio"
                                        className="form-check-input me-3"
                                        name="plan"
                                        checked={planCode === plan.code}
                                        onChange={() => {
                                            setPlanCode(plan.code);
                                            ReactGA.event({ category: 'Signup', action: 'Plan Selected', label: plan.code });
                                        }}
                                    />
                                    <strong>{plan.title}</strong>
                                    <div className="text-muted" style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                                        {plan.meetingsIncluded === null
                                            ? 'Unlimited regatta creation while active.'
                                            : `${plan.meetingsIncluded} regatta creation included.`}
                                    </div>
                                </div>
                                <div className="signup-plan-price" style={{ fontWeight: 700, textAlign: 'right' }}>
                                    GBP {Number(plan.amount || 0).toFixed(2)}
                                    {plan.recurring && <div style={{ fontSize: '0.85rem', fontWeight: 'normal' }}>/year</div>}
                                </div>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="form-check mb-3 signup-privacy-check">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id="privacyAccepted"
                        checked={privacyAccepted}
                        onChange={(e) => {
                            setPrivacyAccepted(e.target.checked);
                            setError('');
                        }}
                    />
                    <label className="form-check-label" htmlFor="privacyAccepted">
                        I confirm that I have read the <Link to="/privacy-policy" target="_blank" rel="noreferrer">Privacy Policy</Link> and understand how RegattaManager will use my personal data to create and manage my account and subscription.
                    </label>
                </div>

                <div className="signup-actions-row mt-4 d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary" onClick={handleBack}>
                        Back
                    </button>
                    <button type="submit" className="btn btn-primary flex-grow-1" disabled={submitting || loadingPlans}>
                        {submitting ? 'Processing...' : 'Continue to Payment'}
                    </button>
                </div>
            </form>
        </div>
    );

    return (
        <section className="signup-page-wrap">
            <div className="signup-card">
                <div className="signup-brand-row">
                    <div className="signup-brand-mark" aria-hidden="true">R</div>
                    <h1 className="signup-brand-title">regattamanager</h1>
                </div>

                <p className="signup-subtitle">Create your account</p>

                <div className="signup-step-indicator">Step {currentStep} of 4</div>
                <div className="progress signup-progress-track" style={{ height: '4px' }}>
                    <div className="progress-bar" style={{ width: `${progressPercent}%` }}></div>
                </div>

                <div className="signup-step-content">
                    {currentStep === 1 && renderStep1()}
                    {currentStep === 2 && renderStep2()}
                    {currentStep === 3 && renderStep3()}
                    {currentStep === 4 && renderStep4()}
                </div>

                <p className="signup-footer-note">
                    Already have an account? <Link to="/login">Login</Link>
                </p>
            </div>
        </section>
    );
}
